import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { baseApiURL } from '../../baseUrl';
import {
    FiAward, FiPlus, FiCalendar, FiMapPin, FiUser, FiUsers,
    FiEdit2, FiTrash2, FiX, FiCheckCircle, FiClock
} from 'react-icons/fi';

const EMPTY_FORM = {
    title: '', description: '', date: '', venue: '', trainerName: '', type: 'Other'
};

const TYPE_COLORS = {
    'Aptitude':       'bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    'Coding':         'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800',
    'Soft Skills':    'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
    'Mock Interview': 'bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
    'Other':          'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700',
};

const getSessionStatus = (dateStr) => {
    if (!dateStr) return { label: 'No Date', cls: 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700' };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sessionDate = new Date(dateStr);
    sessionDate.setHours(0, 0, 0, 0);

    if (sessionDate > today) {
        return { label: 'Upcoming', cls: 'bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800' };
    } else if (sessionDate.getTime() === today.getTime()) {
        return { label: 'Today', cls: 'bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800' };
    } else {
        return { label: 'Completed', cls: 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' };
    }
};

const Training = () => {
    const [trainings, setTrainings] = useState([]);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [editingTraining, setEditingTraining] = useState(null);
    const [editForm, setEditForm] = useState(EMPTY_FORM);

    useEffect(() => {
        fetchTrainings();
    }, []);

    const fetchTrainings = async () => {
        try {
            const res = await axios.get(`${baseApiURL()}/placement/training/get`);
            if (res.data.success) setTrainings(res.data.trainings || []);
        } catch (error) { console.error(error); }
    };

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
    const handleEditChange = (e) => setEditForm({ ...editForm, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await axios.post(`${baseApiURL()}/placement/training/add`, formData);
            if (res.data.success) {
                toast.success(res.data.message);
                fetchTrainings();
                setFormData(EMPTY_FORM);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error adding training');
        }
    };

    const openEdit = (training) => {
        setEditingTraining(training);
        setEditForm({
            title: training.title || '',
            description: training.description || '',
            date: training.date ? training.date.slice(0, 10) : '',
            venue: training.venue || '',
            trainerName: training.trainerName || '',
            type: training.type || 'Other',
        });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await axios.put(`${baseApiURL()}/placement/training/update/${editingTraining._id}`, editForm);
            if (res.data.success) {
                toast.success('Training updated successfully');
                fetchTrainings();
                setEditingTraining(null);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error updating training');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this training session? This cannot be undone.')) return;
        try {
            const res = await axios.delete(`${baseApiURL()}/placement/training/delete/${id}`);
            if (res.data.success) {
                toast.success('Training deleted');
                fetchTrainings();
            }
        } catch {
            toast.error('Error deleting training');
        }
    };

    const inputCls = "w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500";
    const labelCls = "text-[10px] font-bold uppercase text-slate-400";

    // Sort: Upcoming first, then today, then past
    const sortedTrainings = [...trainings].sort((a, b) => new Date(a.date) - new Date(b.date));

    return (
        <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
                <div className="bento-header-banner flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-800 p-2 rounded-xl text-indigo-600 dark:text-indigo-400">
                            <FiAward className="text-xl" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Training & Readiness</h1>
                            <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                                Organize, edit, and track coding bootcamps, aptitude sessions, and mock interviews.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {/* Add Training Form */}
                    <form onSubmit={handleSubmit} className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-5 rounded-2xl space-y-4">
                        <div className="flex items-center gap-2 text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                            <FiPlus className="text-indigo-600 dark:text-indigo-400" />
                            <span>Schedule Training Workshop</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            <div className="space-y-1">
                                <label className={labelCls}>Workshop Title *</label>
                                <input type="text" name="title" placeholder="e.g. DSA Bootcamp" value={formData.title} onChange={handleChange} required className={inputCls} />
                            </div>
                            <div className="space-y-1">
                                <label className={labelCls}>Date *</label>
                                <input type="date" name="date" value={formData.date} onChange={handleChange} required className={inputCls} />
                            </div>
                            <div className="space-y-1">
                                <label className={labelCls}>Session Domain</label>
                                <select name="type" value={formData.type} onChange={handleChange} className={inputCls}>
                                    <option value="Aptitude">Aptitude & Reasoning</option>
                                    <option value="Coding">DSA & Technical Coding</option>
                                    <option value="Soft Skills">Soft Skills & Communication</option>
                                    <option value="Mock Interview">Mock HR / Tech Interview</option>
                                    <option value="Other">Other Workshop</option>
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className={labelCls}>Venue</label>
                                <input type="text" name="venue" placeholder="e.g. Seminar Hall / Zoom" value={formData.venue} onChange={handleChange} className={inputCls} />
                            </div>
                            <div className="space-y-1">
                                <label className={labelCls}>Trainer / Speaker</label>
                                <input type="text" name="trainerName" placeholder="e.g. Dr. Rajan Mehta" value={formData.trainerName} onChange={handleChange} className={inputCls} />
                            </div>
                            <div className="space-y-1 lg:col-span-3">
                                <label className={labelCls}>Description (optional)</label>
                                <textarea name="description" value={formData.description} onChange={handleChange} rows={2} placeholder="Topics covered, prerequisites, etc." className={`${inputCls} resize-none`} />
                            </div>
                        </div>
                        <div className="flex justify-end pt-1">
                            <button type="submit" className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl shadow-xs transition cursor-pointer flex items-center gap-1.5">
                                <FiPlus /> <span>Create Session</span>
                            </button>
                        </div>
                    </form>

                    {/* Trainings Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {sortedTrainings.length === 0 ? (
                            <div className="col-span-full text-center py-10 text-slate-400 text-xs font-semibold">
                                No training workshops scheduled yet.
                            </div>
                        ) : (
                            sortedTrainings.map(t => {
                                const status = getSessionStatus(t.date);
                                const typeCls = TYPE_COLORS[t.type] || TYPE_COLORS.Other;

                                return (
                                    <div key={t._id} className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-5 rounded-2xl flex flex-col justify-between hover:shadow-sm transition">
                                        {/* Top */}
                                        <div>
                                            <div className="flex items-start justify-between gap-2">
                                                <h3 className="font-bold text-sm text-slate-900 dark:text-white leading-snug">{t.title}</h3>
                                                <div className="flex flex-col items-end gap-1.5 shrink-0">
                                                    {/* Type badge */}
                                                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border uppercase tracking-wider ${typeCls}`}>
                                                        {t.type}
                                                    </span>
                                                    {/* Status badge (auto from date) */}
                                                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border flex items-center gap-1 ${status.cls}`}>
                                                        {status.label === 'Upcoming' ? <FiClock size={9} /> : <FiCheckCircle size={9} />}
                                                        {status.label}
                                                    </span>
                                                </div>
                                            </div>

                                            {t.description && (
                                                <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed line-clamp-2">
                                                    {t.description}
                                                </p>
                                            )}

                                            <div className="mt-3.5 space-y-1.5 text-xs text-slate-600 dark:text-slate-400 border-t border-slate-200 dark:border-slate-800/80 pt-3">
                                                <p className="flex items-center gap-1.5">
                                                    <FiCalendar size={12} className="text-slate-400" />
                                                    <span className="font-semibold">
                                                        {t.date ? new Date(t.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                                    </span>
                                                </p>
                                                {t.venue && (
                                                    <p className="flex items-center gap-1.5">
                                                        <FiMapPin size={12} className="text-slate-400" />
                                                        <span>{t.venue}</span>
                                                    </p>
                                                )}
                                                {t.trainerName && (
                                                    <p className="flex items-center gap-1.5">
                                                        <FiUser size={12} className="text-slate-400" />
                                                        <span>{t.trainerName}</span>
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Bottom: Registered count + actions */}
                                        <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-800/80 flex items-center justify-between">
                                            <span className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                                                <FiUsers size={12} />
                                                <span className="font-bold text-indigo-600 dark:text-indigo-400 font-mono">
                                                    {t.registeredStudents?.length || 0}
                                                </span>
                                                <span>registered</span>
                                            </span>
                                            <div className="flex items-center gap-1.5">
                                                {/* Edit */}
                                                <button
                                                    onClick={() => openEdit(t)}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 rounded-lg text-[10px] font-bold hover:bg-indigo-100 dark:hover:bg-indigo-900 transition cursor-pointer"
                                                >
                                                    <FiEdit2 size={10} /> Edit
                                                </button>
                                                {/* Delete */}
                                                <button
                                                    onClick={() => handleDelete(t._id)}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800 rounded-lg text-[10px] font-bold hover:bg-rose-100 dark:hover:bg-rose-900 transition cursor-pointer"
                                                >
                                                    <FiTrash2 size={10} /> Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            {/* ─── Edit Training Modal ─── */}
            {editingTraining && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        {/* Modal header */}
                        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-800">
                            <div>
                                <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                    <FiEdit2 className="text-indigo-600 dark:text-indigo-400" size={16} />
                                    Edit Training Session
                                </h2>
                                <p className="text-xs text-slate-400 mt-0.5">{editingTraining.title}</p>
                            </div>
                            <button
                                onClick={() => setEditingTraining(null)}
                                className="p-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-slate-600 dark:text-slate-400 transition cursor-pointer"
                            >
                                <FiX size={16} />
                            </button>
                        </div>

                        {/* Modal form */}
                        <form onSubmit={handleEditSubmit} className="p-5 space-y-3">
                            <div className="space-y-1">
                                <label className={labelCls}>Workshop Title *</label>
                                <input type="text" name="title" value={editForm.title} onChange={handleEditChange} required className={inputCls} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                    <label className={labelCls}>Date *</label>
                                    <input type="date" name="date" value={editForm.date} onChange={handleEditChange} required className={inputCls} />
                                </div>
                                <div className="space-y-1">
                                    <label className={labelCls}>Session Domain</label>
                                    <select name="type" value={editForm.type} onChange={handleEditChange} className={inputCls}>
                                        <option value="Aptitude">Aptitude & Reasoning</option>
                                        <option value="Coding">DSA & Technical Coding</option>
                                        <option value="Soft Skills">Soft Skills & Communication</option>
                                        <option value="Mock Interview">Mock HR / Tech Interview</option>
                                        <option value="Other">Other Workshop</option>
                                    </select>
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className={labelCls}>Venue</label>
                                <input type="text" name="venue" placeholder="e.g. Seminar Hall / Zoom" value={editForm.venue} onChange={handleEditChange} className={inputCls} />
                            </div>
                            <div className="space-y-1">
                                <label className={labelCls}>Trainer / Speaker</label>
                                <input type="text" name="trainerName" placeholder="e.g. Dr. Rajan Mehta" value={editForm.trainerName} onChange={handleEditChange} className={inputCls} />
                            </div>
                            <div className="space-y-1">
                                <label className={labelCls}>Description</label>
                                <textarea name="description" value={editForm.description} onChange={handleEditChange} rows={3} placeholder="Topics covered, prerequisites, etc." className={`${inputCls} resize-none`} />
                            </div>

                            <div className="flex justify-end gap-2 pt-2">
                                <button type="button" onClick={() => setEditingTraining(null)}
                                    className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-bold rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition cursor-pointer">
                                    Cancel
                                </button>
                                <button type="submit"
                                    className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl shadow-xs transition cursor-pointer flex items-center gap-1.5">
                                    <FiCheckCircle size={13} /> Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Training;
